import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { UnitBreakdown } from '@/components/analytics/UnitBreakdown'
import { SectionCard } from '@/components/warehouse/primitives'
import type { InventoryVariant } from '@/lib/api/schemas/inventory.schema'
import { fetchUnitEconomicsPiece, formatPerGram, perGram, signClass, type UnitEconomicsRow } from '@/lib/unit-economics'
import { formatRubleAmount } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'

type LoadState =
  | { key: string; kind: 'loading' }
  | { key: string; kind: 'ready'; row: UnitEconomicsRow | null }
  | { key: string; kind: 'failed' }

interface Figure {
  label: string
  value: number | null
  weight: number | null
  tone?: string
  reason?: string | null
}

interface BarSegment {
  name: string
  amount: number
  color: string
}

function dateLabel(value: string | null): string {
  if (value === null) return '—'
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' })
}

function FigureValue({ figure }: { figure: Figure }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-ink-muted">{figure.label}</dt>
      <dd className={`mt-1 text-xl leading-tight tab-nums ${figure.tone ?? 'text-ink'}`}>
        {figure.value === null ? '—' : formatRubleAmount(figure.value)}
      </dd>
      <dd className="mt-0.5 text-xs tab-nums text-ink-faint">
        {figure.value === null
          ? figure.reason ?? ''
          : formatPerGram(perGram(figure.value, figure.weight))}
      </dd>
    </div>
  )
}

function BarSegmentView({ segment, left, width }: { segment: BarSegment; left: number; width: number }) {
  const amount = formatRubleAmount(segment.amount)
  return (
    <span
      role="img"
      tabIndex={0}
      aria-label={`${segment.name}: ${amount}`}
      title={`${segment.name}: ${amount}`}
      className={`absolute inset-y-0 rounded-sm outline-none focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-gold-deep focus-visible:ring-offset-1 ${segment.color}`}
      style={{ left: `${left}%`, width: `${width}%` }}
    />
  )
}

function MoneyBar({ segments, denominator }: { segments: readonly BarSegment[]; denominator: number }) {
  if (!Number.isFinite(denominator) || denominator <= 0 || segments.some((segment) => !Number.isFinite(segment.amount))) return null
  const loss = segments.at(-1)?.amount ?? 0
  const overflowing = loss < 0
  const positiveSegments = segments.map((segment) => ({ ...segment, amount: Math.max(segment.amount, 0) }))
  const costTotal = positiveSegments.reduce((total, segment) => total + segment.amount, 0)
  const scale = overflowing ? costTotal : denominator
  if (scale <= 0) return null

  let left = 0
  const regular = positiveSegments.filter((_, index) => !overflowing || index < positiveSegments.length - 1).map((segment) => {
    const width = segment.amount / scale * 100
    const result = { segment, left, width }
    left += width
    return result
  })
  const marker = overflowing ? Math.min(100, denominator / scale * 100) : null
  const overflowWidth = overflowing ? Math.max(0, -loss / scale * 100) : 0
  return (
    <div data-testid="economics-bar" role="group" aria-label="Распределение суммы">
      <div className="relative h-2 w-full overflow-visible rounded-sm bg-ink-faint/15">
        {regular.map(({ segment, left: segmentLeft, width }) => (
          <BarSegmentView key={segment.name} segment={segment} left={segmentLeft} width={width} />
        ))}
        {overflowing && marker !== null ? (
          <>
            <BarSegmentView segment={{ ...segments.at(-1)!, color: 'bg-[#b3261e]' }} left={marker} width={overflowWidth} />
            <span aria-hidden="true" className="absolute inset-y-[-2px] z-10 w-px bg-ink" style={{ left: `${marker}%` }} />
          </>
        ) : null}
      </div>
    </div>
  )
}

function SoldBar({ row }: { row: UnitEconomicsRow }) {
  if (row.cost === null || row.cdekCost === null || row.tax === null || row.profit === null) return null
  return (
    <MoneyBar
      denominator={row.soldFor}
      segments={[
        { name: 'закупка', amount: row.cost, color: 'bg-ink/70' },
        { name: 'удержания ЯМ', amount: row.marketWithheld, color: 'bg-gold-deep' },
        { name: 'СДЭК', amount: row.cdekCost, color: 'bg-ink-faint/60' },
        { name: 'налог', amount: row.tax, color: 'bg-ink-faint/35' },
        { name: 'прибыль', amount: row.profit, color: row.profit < 0 ? 'bg-[#b3261e]' : 'bg-[#1f7a3d]' },
      ]}
    />
  )
}

function StockBar({ cost, inHand }: { cost: number; inHand: number }) {
  return (
    <MoneyBar
      denominator={inHand}
      segments={[
        { name: 'закупка', amount: cost, color: 'bg-ink/70' },
        { name: 'разница', amount: inHand - cost, color: inHand >= cost ? 'bg-[#1f7a3d]' : 'bg-[#b3261e]' },
      ]}
    />
  )
}

function SoldEconomics({ row }: { row: UnitEconomicsRow }) {
  const [open, setOpen] = useState(false)
  const figures: Figure[] = [
    { label: 'Купили за', value: row.cost, weight: row.weightGrams, reason: row.costReason },
    { label: 'Продали за', value: row.soldFor, weight: row.weightGrams },
    { label: 'Прибыль', value: row.profit, weight: row.weightGrams, tone: signClass(row.profit) },
  ]
  return (
    <SectionCard
      title="Экономика"
      action={(
        <Link
          to={`/orders?orderId=${encodeURIComponent(row.orderId)}`}
          className="text-[11px] text-ink-muted underline decoration-rule underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep"
        >
          заказ {row.orderId}, {dateLabel(row.orderCreatedAt)}
        </Link>
      )}
    >
      <dl className="grid grid-cols-2 gap-x-3 gap-y-3 min-[360px]:grid-cols-3">
        {figures.map((figure) => <FigureValue key={figure.label} figure={figure} />)}
      </dl>
      {row.cost !== null ? <div className="mt-4"><SoldBar row={row} /></div> : null}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="mt-3 text-[11px] text-gold-deep underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep"
      >
        {open ? 'Скрыть расчёт' : 'Показать расчёт'}
      </button>
      <div className={`overflow-hidden transition-[max-height,opacity] duration-200 motion-reduce:transition-none ${open ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'}`}>
        {open ? <UnitBreakdown row={row} /> : null}
      </div>
    </SectionCard>
  )
}

function StockEconomics({ variant }: { variant: InventoryVariant }) {
  const economics = variant.ymarketEconomics
  const cost = economics?.purchaseCost ?? null
  const costReason = economics?.earnings.unavailableReason
    ? unavailableReasonText(economics.earnings.unavailableReason)
    : 'Нет себестоимости в 1С'
  const figures: Figure[] = [{ label: 'Купили за', value: cost, weight: economics?.weightGrams ?? variant.attributes.netWeightGrams, reason: costReason }]
  const inHand = economics?.net ?? null
  if (economics !== null && cost !== null && inHand !== null) {
    figures.push(
      { label: 'На руки с витрины ЯМ', value: inHand, weight: economics.weightGrams },
      { label: 'Разница', value: inHand - cost, weight: economics.weightGrams, tone: signClass(inHand - cost) },
    )
  }
  return (
    <SectionCard title="Экономика">
      <dl className="grid grid-cols-2 gap-x-3 gap-y-3 min-[360px]:grid-cols-3">
        {figures.map((figure) => <FigureValue key={figure.label} figure={figure} />)}
      </dl>
      {cost !== null && inHand !== null ? <div className="mt-4"><StockBar cost={cost} inHand={inHand} /></div> : null}
    </SectionCard>
  )
}

export function ProductEconomics({ variant }: { variant: InventoryVariant }) {
  const [load, setLoad] = useState<LoadState>({ key: variant.productId, kind: 'loading' })

  useEffect(() => {
    const key = variant.productId
    const controller = new AbortController()
    setLoad({ key, kind: 'loading' })
    fetchUnitEconomicsPiece(key, controller.signal)
      .then((row) => { if (!controller.signal.aborted) setLoad({ key, kind: 'ready', row }) })
      .catch(() => { if (!controller.signal.aborted) setLoad({ key, kind: 'failed' }) })
    return () => controller.abort()
  }, [variant.productId])

  const current = load.key === variant.productId ? load : { key: variant.productId, kind: 'loading' as const }
  if (current.kind === 'failed') {
    return <SectionCard title="Экономика"><p className="text-xs text-ink-faint">Экономика недоступна</p></SectionCard>
  }
  if (current.kind === 'loading') {
    return <SectionCard title="Экономика"><p role="status" className="text-xs text-ink-muted">Загружаем экономику…</p></SectionCard>
  }
  return current.row === null ? <StockEconomics variant={variant} /> : <SoldEconomics row={current.row} />
}
